
import {
    Flex,
    Text,
    Button as ChakraButton,
    VStack,
    Input,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CheckCircleIcon2, CloseCircleIcon } from "@/styles/icons";
import { PixMiniIcon } from "@/styles/icons/PixMiniIcon";
import { BankSlipMiniIcon } from "@/styles/icons/BankSlipMiniIcon";
import currency from "currency.js";
import { useCurrentBalance } from "@/hooks/useCurrentBalance";
import { formatCurrency, formatCurrencyToApi, detectPixKeyType, hasCnpjShape, isValidCnpj, isValidCpf, normalizePhoneForAPI, showToast, parsePixQRCode, getPaymentErrorMessage } from "@/utils";
import { usePayment } from "@/hooks/usePayment";
import { PixKeySelector } from "../selector-chave-pix";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PixConfirmStep } from "../confirm-pix";
import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { BankSlipReviewStep } from "../review-boleto";
import { BankSlipConfirmStep } from "../confirm-boleto";
import { BankSlipInput } from "@/components/Forms/BankSlipInput";
import { Drawer, Loading } from "barte-design-system";
import { InputCurrency } from "@/components";

const paymentSchema = z.object({
    amount: z
        .string()
        .min(1, "Informe um valor")
        .refine((val) => formatCurrencyToApi(val) > 0, "O valor deve ser maior que zero"),
    pixKey: z
        .string()
        .trim()
        .optional(),
    pixQRCode: z
        .string()
        .trim()
        .optional(),
    isFavorite: z.boolean().optional(),
}).refine(
    (data) => {
        // Precisa ter chave PIX ou QR Code
        if (!data.pixKey && !data.pixQRCode) {
            return false;
        }
        return true;
    },
    {
        message: "Informe a chave Pix ou cole o código QR Code",
        path: ["pixKey"],
    }
).refine(
    (data) => {
        // Se tem QR Code no campo pixQRCode, valida o formato
        if (data.pixQRCode && !data.pixQRCode.startsWith("000201")) {
            return false;
        }
        // Se tem QR Code no campo pixKey (colado diretamente), valida o formato
        if (data.pixKey && data.pixKey.trim().startsWith("000201") && !data.pixKey.trim().match(/^000201.*/)) {
            return false;
        }
        return true;
    },
    {
        message: "QR Code PIX inválido",
        path: ["pixKey"],
    }
).refine(
    (data) => {
        const isQRCode = (data.pixKey && data.pixKey.trim().startsWith("000201")) || (data.pixQRCode && data.pixQRCode.startsWith("000201"));
        if (isQRCode) {
            return true;
        }
        // Se não é QR Code, valida a chave PIX normalmente
        if (data.pixKey && !data.pixQRCode) {
            const numeric = data.pixKey.replaceAll(/\D/g, "");
            // Email
            if (/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(data.pixKey)) return true;
            // Telefone: 10 ou 11 dígitos com DDD válido
            if (numeric.length === 10 || numeric.length === 11) {
                const areaCode = numeric.substring(0, 2);
                const areaCodeNum = parseInt(areaCode);
                if (areaCodeNum >= 11 && areaCodeNum <= 99) {
                    if (numeric.length === 11 && numeric.charAt(2) === '9') return true;
                    if (numeric.length === 10) return true;
                }
            }
            // CPF
            if (/^[0-9]{11}$/.test(numeric)) return true;
            // CNPJ
            if (hasCnpjShape(data.pixKey)) return true;
            // Chave aleatória (UUID)
            if (/^[0-9a-fA-F]{32}$/.test(data.pixKey)) return true;
            if (/^[0-9a-fA-F\-]{36}$/.test(data.pixKey)) return true;
            // Chave aleatória (texto)
            if (/^[A-Za-zÀ-ÿ\s]{3,}$/.test(data.pixKey)) return true;
            return false;
        }
        return true;
    },
    "Formato de chave Pix inválido"
).refine((data) => {
    const isQRCode = (data.pixKey && data.pixKey.trim().startsWith("000201")) || (data.pixQRCode && data.pixQRCode.startsWith("000201"));
    if (isQRCode) {
        return true;
    }
    
    const numeric = data.pixKey?.replaceAll(/\D/g, "") || "";
    if (numeric.length === 11 && data.pixKey) {
        const areaCode = numeric.substring(0, 2);
        const areaCodeNum = parseInt(areaCode);
        if (areaCodeNum >= 11 && areaCodeNum <= 99 && numeric.charAt(2) === '9') {
            return true;
        }
        return isValidCpf(numeric);
    }
    // Valida CNPJ
    if (data.pixKey && hasCnpjShape(data.pixKey)) return isValidCnpj(data.pixKey);
    return true;
}, "CPF ou CNPJ inválido");

const bankSlipPaymentSchema = z.object({
    barcode: z
        .string()
        .trim()
        .min(1, "Informe o código de barras")
        .refine(
            (val) => /^\d+$/.test(val.replace(/\s/g, "")),
            "O código de barras deve conter apenas números"
        )
        .refine(
            (val) => {
            const clean = val.replace(/\s/g, "");
            return clean.length === 44 || clean.length === 47;
            },
            "O código de barras deve ter 44 ou 47 dígitos"
        ),
    paymentDate: z.date(),
    description: z.string().min(1, "Informe a descrição do boleto"),
    amount: z.string().min(1, "Informe o valor do boleto"),
});

type PaymentFormData = z.infer<typeof paymentSchema>;
type BankSlipPaymentFormData = z.infer<typeof bankSlipPaymentSchema>;

interface DrawerPaymentInvoiceProps {
    isOpen: boolean;
    onClose: () => void;
}

export function DrawerNewPayment({
    isOpen,
    onClose,
}: DrawerPaymentInvoiceProps) {
    const [section, setSection] = useState<"CHOOSE_METHOD" | "PAYMENT_FLOW" | "ERROR" | "SUCCESS" | "ANTIFRAUD_ANALYSIS">("CHOOSE_METHOD");
    const [method, setMethod] = useState<"PIX" | "BANK_SLIP" | "PIX_COPY_PASTE" | null>(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [pixQRCode, setPixQRCode] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<{ title?: string; message: string } | null>(null);


    const { currentBalance } = useCurrentBalance();
    const { 
        getListPixFavoritesInitialData, 
        pixFavorites, 
        pixRecipient,
        bankSlipData,
        checkPayment,
        processPayment,
    } = usePayment();

    const { execute: executeCheckPayment, loading: loadingCheckPayment} = useAsyncFunction(checkPayment);
    const { execute: executeProcessPayment, loading: loadingProcessPayment} = useAsyncFunction(processPayment);
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
      } = useForm<PaymentFormData>({
        resolver: zodResolver(paymentSchema),
        defaultValues: {
          amount: "",
          pixKey: "",
          pixQRCode: "",
        },
        mode: "onChange",
    });

    const {
        watch: watchBankSlip,
        setValue: setValueBankSlip,
        formState: { errors: errorsBankSlip },
      } = useForm<BankSlipPaymentFormData>({
        resolver: zodResolver(bankSlipPaymentSchema),
        defaultValues: {
            barcode: "",
        },
        mode: "onChange",
    });


    const isPixPaymentFlow = section === "PAYMENT_FLOW" && method === "PIX";
    const isBankSlipPaymentFlow = section === "PAYMENT_FLOW" && method === "BANK_SLIP";
    const amountValue = watch("amount");
    const pixKeyValue = watch("pixKey");
    //Steps de pagamento baseado no metodo.
    const FLOW_MAP = {
        PIX: ["PIX_KEY", "PIX_AMOUNT", "PIX_LOADING", "PIX_CONFIRM"],
        BANK_SLIP: ["BANK_SLIP_BARCODE", "BANK_SLIP_LOADING", "BANK_SLIP_REVIEW", "BANK_SLIP_CONFIRM"],
    };
    const handleClearInputs = () => {
        setValue("amount", "");
        setValue("pixKey", "");
        setValue("pixQRCode", "");
        setPixQRCode("");
        setValueBankSlip("barcode", "");
        setValueBankSlip("paymentDate", null);
        setValueBankSlip("description", "");
        setValueBankSlip("amount", "");
    }
    const handleCloseDrawer = () => {
        onClose();
        handleClearInputs();
    }

    const handleValidadePayment = async (type: "PIX" | "BANK_SLIP" | "COPY_PASTE_PIX", data?: any) => {
        if (type === "PIX") {
            const amount = formatCurrencyToApi(amountValue);
            setCurrentStepIndex(2); // PIX_LOADING
            try {
                let normalizedPixKey = data.pixKey;
                if (detectPixKeyType(data.pixKey) === "PHONE") {
                    normalizedPixKey = normalizePhoneForAPI(data.pixKey);
                }
                await executeCheckPayment({
                    pixKey: normalizedPixKey,
                    pixAmount: amount,
                    type: "PIX",
                    isFavorite: data.isFavorite,
                });
                setCurrentStepIndex(3); // PIX_CONFIRM
            } catch (error: any) {
                const errorMapping = getPaymentErrorMessage(error);
                setErrorMessage(errorMapping);
                setSection("ERROR");
            } 
        }
        if (type === "COPY_PASTE_PIX") {
            const amount = typeof data.pixAmount === "number" ? data.pixAmount : formatCurrencyToApi(data.pixAmount);
            setCurrentStepIndex(2); // PIX_LOADING 
            try {
                await executeCheckPayment({
                    pixKey: data.pixQRCode, // Envia o QR Code completo
                    pixAmount: amount,
                    type: "COPY_PASTE_PIX",
                    description: data.description,
                });
                setCurrentStepIndex(3); // PIX_CONFIRM
            } catch (error: any) {
                const errorMapping = getPaymentErrorMessage(error);
                setErrorMessage(errorMapping);
                setSection("ERROR");
            }
        }
        if (type === "BANK_SLIP") {
            setMethod("BANK_SLIP")
            setCurrentStepIndex(1); // BANK_SLIP_LOADING
            try {
                await executeCheckPayment({
                    // Clear barcode
                    barCode: data.barcode.replace(/\D/g, ""),
                    type: "BANK_SLIP",
                });
                setCurrentStepIndex(3);
            } catch (error: any) {
                const errorMapping = getPaymentErrorMessage(error);
                showToast({
                    message: errorMapping.message,
                    type: "error",
                });
                setCurrentStepIndex(0);
                handleClearInputs();
            }
        }
    }

    const handleNextStep = (data?: any) => {
        if (section === "CHOOSE_METHOD" && method) {
          setSection("PAYMENT_FLOW");
          setCurrentStepIndex(0);
          return;
        }
    
        if (isPixPaymentFlow) {
          const flow = FLOW_MAP.PIX;
          const step = flow[currentStepIndex];
            if (step === "PIX_KEY") {
                const inputValue = data?.pixKey || pixKeyValue || watch("pixKey");
                if (!inputValue || errors.pixKey) return;
                if (inputValue.trim().startsWith("000201")) {
                    const qrCode = inputValue.trim();
                    const parsedQR = parsePixQRCode(qrCode);
                    if (!parsedQR.success) {
                        showToast({
                            message: parsedQR.error || "QR Code inválido",
                            type: "error",
                        });
                        return;
                    }
                    // Salva o QR Code
                    setValue("pixQRCode", qrCode);
                    setPixQRCode(qrCode);
                    if (parsedQR.amount && parsedQR.amount > 0) {
                        setValue("amount", formatCurrency({ value: parsedQR.amount }));
                        setCurrentStepIndex(2); // PIX_LOADING
                        handleValidadePayment("COPY_PASTE_PIX", {
                            pixQRCode: qrCode,
                            pixAmount: parsedQR.amount,
                            description: parsedQR.description || "",
                        });
                        return;
                    }
                    // Se não tem valor, vai para step de digitar valor
                    setCurrentStepIndex(1); // PIX_AMOUNT
                    return;
                }
                setCurrentStepIndex(1); // PIX_AMOUNT
                return;
            }
            if (step === "PIX_AMOUNT") {
                const parsed = formatCurrencyToApi(data?.amount || amountValue);
                if (parsed > currentBalance) return;
                if (parsed === 0) return;
                const qrCode = pixQRCode || watch("pixQRCode");
                if (qrCode && qrCode.startsWith("000201")) {
                    setCurrentStepIndex(2); // PIX_LOADING
                    handleValidadePayment("COPY_PASTE_PIX", {
                        pixQRCode: qrCode,
                        pixAmount: parsed,
                        description: "",
                    });
                } else {
                    setCurrentStepIndex(2); // PIX_LOADING
                    handleValidadePayment("PIX", { 
                        pixKey: watch("pixKey") || pixKeyValue, 
                        isFavorite: watch("isFavorite") || false 
                    });
                }
                return;
            }

            // PIX_CONFIRM (índice 3) -> finaliza o pagamento
            if (step === "PIX_CONFIRM") {
                handleSubmitPixPayment();
            }
        }
        if (isBankSlipPaymentFlow) {
            const flow = FLOW_MAP.BANK_SLIP;
            const step = flow[currentStepIndex];
            if (step === "BANK_SLIP_BARCODE" && !errorsBankSlip.barcode) {
                handleValidadePayment("BANK_SLIP", { barcode: watchBankSlip("barcode") });
                return
            }
            if (step === "BANK_SLIP_CONFIRM") {
                handleSubmitBankSlipPayment();
                return
            }
            setCurrentStepIndex((i) => i + 1);
          }

    };

    const handleBack = () => {
        if (section === "CHOOSE_METHOD") {
            onClose();
            return;
        }

        if (section === "PAYMENT_FLOW") {
            const PIX_CONFIRM_STEP_INDEX = 3; // PIX_CONFIRM
            const PIX_BARCODE_REVIEW_STEP_INDEX = 2;
            if (currentStepIndex === 0) {
                setSection("CHOOSE_METHOD");
                return;
            }
            if (method === "BANK_SLIP" && currentStepIndex === PIX_BARCODE_REVIEW_STEP_INDEX) {
                setCurrentStepIndex(0);
                return;
            }
            if (method === "PIX" && currentStepIndex === PIX_CONFIRM_STEP_INDEX) {
                setCurrentStepIndex(1); // PIX_AMOUNT
                return;
            }
            setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
        }
    };

    async function handleSubmitPixPayment() {
        if (!pixRecipient) return;  
        try {
            const response = await executeProcessPayment("PIX", pixRecipient);
            showToast({
                message: 'Pagamento PIX processado',
                type: "success",
            });
            if(response.status === "AWAITING_ANTIFRAUD_ANALYSIS") {
                setSection("ANTIFRAUD_ANALYSIS");
            } else {
                setSection("SUCCESS");  
            }
    
        } catch (error: any) {
            const errorMapping = getPaymentErrorMessage(error);
            setErrorMessage(errorMapping);
            setSection("ERROR");
            throw error;
        } finally {
            handleClearInputs();
        }
    }

    async function handleSubmitBankSlipPayment() {
        if (!bankSlipData) return;
        try {
            await executeProcessPayment("BANK_SLIP", bankSlipData);
            setSection("SUCCESS");
        } catch (error: any) {
            const errorMapping = getPaymentErrorMessage(error);
            setErrorMessage(errorMapping);
            setSection("ERROR");
            throw error;
        } finally {
            handleClearInputs();
        }
    }

    function formatCurrencyText(value: number) {
        return currency(value, { symbol: "R$ ", separator: ".", decimal: ",", precision: 2 }).format();
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/[^\d]/g, "");
        if (val.length === 0) {
          setValue("amount", "");
          return;
        }
        val = (Number(val) / 100).toFixed(2);
        setValue("amount", formatCurrencyText(Number(val)));
    };

    const handlePixFavoriteSelect = (fav: any) => {
        setValue("pixKey", fav.pixKey);
        setValue("isFavorite", true);
        setCurrentStepIndex(1); // PIX_AMOUNT
    };

    const isContinueButtonDisable = (): boolean => {
        if(section === "CHOOSE_METHOD" && !method) return true;
        if(isPixPaymentFlow && FLOW_MAP.PIX[currentStepIndex] === "PIX_KEY" && !pixKeyValue && !errors.pixKey) return true;
        if(isPixPaymentFlow && FLOW_MAP.PIX[currentStepIndex] === "PIX_AMOUNT" && ((formatCurrencyToApi(amountValue) > currentBalance) || (formatCurrencyToApi(amountValue) === 0))) return true;
        return false;
    }

    useEffect(()=> {
        getListPixFavoritesInitialData();
    }, [])

    useEffect(() => {
        if(isOpen) {
            setMethod(null);
            setSection("CHOOSE_METHOD");
            setErrorMessage(null);
        }
    }, [isOpen])

    const handleDoubleClick = (event: React.MouseEvent<HTMLButtonElement>, method: "PIX" | "BANK_SLIP" | "PIX_COPY_PASTE") => {
        setMethod(method);
        if(event.detail === 2) {
            handleNextStep();
        }
    }



    return (
        <Drawer
            title="Novo pagamento"
            isOpen={isOpen}
            onClose={handleCloseDrawer}
            footer={
                section === "SUCCESS" || section === "ERROR" || section === "ANTIFRAUD_ANALYSIS"
                    ? { primaryButton: { children: "Fechar", variant: "outline", onClick: handleCloseDrawer } }
                    : {
                        secondaryButton: { children: "Voltar", variant: "outline", onClick: handleBack },
                        primaryButton: {
                            children: section === "PAYMENT_FLOW" && method && currentStepIndex === FLOW_MAP[method as "PIX" | "BANK_SLIP"].length - 1
                                ? "Finalizar pagamento" : "Continuar",
                            onClick: handleNextStep,
                            disabled: isContinueButtonDisable() || loadingCheckPayment || loadingProcessPayment,
                            loading: loadingCheckPayment || loadingProcessPayment,
                        },
                    }
            }
        >
                    <Flex
                        direction="column"
                        bg="white"
                        borderRadius="lg"
                        w="100%"
                        h="100%"
                    >
                        <>
                            {section === "CHOOSE_METHOD" && (
                                <>
                                    <Flex
                                        direction="column" 
                                        gap={4}
                                        h="100%"
                                        w="100%"
                                        justify="center"
                                    >
                                        {/* Botões de pagamento */}
                                        <VStack w="full" spacing={3}>
                                            <ChakraButton
                                                w="full"
                                                variant="outline"
                                                justifyContent="flex-start"
                                                h="80px"
                                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleDoubleClick(event, "BANK_SLIP")}
                                                borderColor={method === "BANK_SLIP" ? "pink.200" : ""}
                                            >
                                                <Flex 
                                                    w="100%"
                                                    align="center"
                                                    justify="center"
                                                    gap="3"
                                                    color={method === "BANK_SLIP" ? "pink.200" : ""}
                                                >
                                                    <BankSlipMiniIcon
                                                        size={24}
                                                        color={method === "BANK_SLIP" ? "#CB2159" : "#000"}
                                                    />
                                                    <Text fontWeight="medium">Pagamento de boleto</Text>
                                                </Flex>
                                            </ChakraButton>
                                            <ChakraButton
                                                w="full"
                                                variant="outline"
                                                justifyContent="flex-start"
                                                h="80px"
                                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleDoubleClick(event, "PIX")}
                                                borderColor={method === "PIX" ? "pink.200" : ""}
                                            >
                                                <Flex 
                                                    w="100%"
                                                    align="center"
                                                    justify="center"
                                                    gap="3"
                                                    color={method === "PIX" ? "pink.200" : ""}
                                                >
                                                    <PixMiniIcon
                                                        size={24}
                                                        color={method === "PIX" ? "#CB2159" : "#000"}
                                                    />
                                                    <Text fontWeight="medium">Pagamento Pix</Text>
                                                </Flex>
                                            </ChakraButton>
                                        </VStack>                                 
                                    </Flex>
                                </>
                            )}
                            {(isPixPaymentFlow) && (
                                <>
                                    {FLOW_MAP.PIX[currentStepIndex] === "PIX_KEY" && (
                                        <form 
                                            onSubmit={handleSubmit(handleNextStep)}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        >
                                            <Controller
                                            name="pixKey"
                                            control={control}
                                            render={({ field }) => (
                                                <PixKeySelector
                                                    pixFavorites={pixFavorites}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    onSelect={handlePixFavoriteSelect}
                                                    error={errors.pixKey?.message}
                                                />
                                            )}
                                         />
                                       </form>
                                    )}

                                    {FLOW_MAP.PIX[currentStepIndex] === "PIX_AMOUNT" && (
                                        <Flex
                                            direction="column" 
                                            gap={4}
                                            h="100%"
                                            w="100%"
                                            justify="center"
                                        >
                                            <VStack align="flex-start" spacing={4}>
                                                <Flex
                                                    w="100%"
                                                    flexDir="column"
                                                >
                                                    <Text fontWeight="semibold">Qual o valor da transferência?</Text>
                                                    <Text color="gray.500">Saldo da conta {formatCurrency({ value: currentBalance })}</Text>
                                                </Flex>
                                                <Flex
                                                    w="100%"
                                                    flexDir="column"
                                                    gap="2"
                                                >
                                                    <Text>Valor</Text>
                                                    <InputCurrency
                                                        name="amount"
                                                        placeholder="R$ 0,00"
                                                        value={amountValue}
                                                        errors={errors.amount}
                                                        onChange={handleChange}
                                                    />
                                                </Flex>
                                            </VStack>
                                        </Flex>
                                    )}

                                    {FLOW_MAP.PIX[currentStepIndex] === "PIX_LOADING" && (
                                        <Flex
                                            w="100%"
                                            h="100%"
                                            justify="center"
                                            align="center"
                                        >
                                            <Flex align="center" justify="center" h="300px" flexDir="column">
                                                <Loading type="page" description="Carregando dados da Chave Pix" />
                                            </Flex>
                                        </Flex>
                                    )}
                                    {FLOW_MAP.PIX[currentStepIndex] === "PIX_CONFIRM" && pixRecipient && (
                                        <PixConfirmStep
                                            onEditAmount={() => setCurrentStepIndex(1)}
                                        />
                                    )}
                                </>
                            )}
                            {(isBankSlipPaymentFlow) && (
                                <>
                                    {FLOW_MAP.BANK_SLIP[currentStepIndex] === "BANK_SLIP_BARCODE" && (
                                        <Flex
                                            direction="column" 
                                            gap={4}
                                            h="100%"
                                            w="100%"
                                            justify="center"
                                        >
                                            <VStack align="flex-start" spacing={4}>
                                                <Flex
                                                    w="100%"
                                                    flexDir="column"
                                                >
                                                    <Text 
                                                        fontSize="22px" 
                                                        fontWeight="400" 
                                                        color="#000"
                                                    >
                                                        Informe o código de barras do boleto que você quer pagar
                                                    </Text>
                                                    <Text 
                                                        fontSize="12px" 
                                                        color="gray.500"
                                                    >
                                                        Os números devem estar próximos ao código de barras
                                                    </Text>
                                                </Flex>
                                                <Flex
                                                    w="100%"
                                                    flexDir="column"
                                                    gap="2"
                                                >
                                                    <Text>Código de barras</Text>
                                                    <BankSlipInput
                                                        value={watchBankSlip("barcode")}
                                                        onChange={(value) => setValueBankSlip("barcode", value)}
                                                    />
                                                </Flex>
                                            </VStack>
                                        </Flex>
                                    )}
                                    {FLOW_MAP.BANK_SLIP[currentStepIndex] === "BANK_SLIP_LOADING" && (
                                        <Flex
                                            w="100%"
                                            h="100%"
                                            justify="center"
                                            align="center"
                                        >
                                            <Flex align="center" justify="center" h="300px" flexDir="column">
                                                <Loading type="page" description="Carregando dados do boleto" />
                                            </Flex>
                                        </Flex>
                                    )}
                                    {FLOW_MAP.BANK_SLIP[currentStepIndex] === "BANK_SLIP_REVIEW" && (
                                        <BankSlipReviewStep />
                                    )}
                                    {FLOW_MAP.BANK_SLIP[currentStepIndex] === "BANK_SLIP_CONFIRM" && (
                                        <BankSlipConfirmStep />
                                    )}
                                </>
                            )}
                            {section === "ERROR" && (
                                <>
                                    <Flex
                                        w="100%"
                                        h="100%"
                                        align="center"
                                        justify="center"
                                        flexDir="column"
                                        gap="4"
                                        p={4}
                                    >
                                        <CloseCircleIcon size={32} />
                                        {errorMessage?.title && (
                                            <Text
                                                maxW="268px"
                                                w="100%"
                                                fontSize="20px"
                                                fontWeight="semibold"
                                                textAlign="center"
                                            >
                                                {errorMessage.title}
                                            </Text>
                                        )}
                                        <Text
                                            maxW="268px"
                                            w="100%"
                                            fontSize={errorMessage?.title ? "14px" : "20px"}
                                            textAlign="center"
                                            color={errorMessage?.title ? "gray.600" : "inherit"}
                                        >
                                            {errorMessage?.message}
                                        </Text>
                                    </Flex>
                                </>
                            )}
                            {section === "SUCCESS" && (
                                <>
                                    <Flex
                                        w="100%"
                                        h="100%"
                                        align="center"
                                        justify="center"
                                        flexDir="column"
                                        gap="4"
                                        p={4}
                                    >
                                        <CheckCircleIcon2 w={8} h={8} />
                                        <Text
                                            maxW="268px"
                                            w="100%"
                                            fontSize="20px"
                                            textAlign="center"
                                        >
                                            Pagamentos processado com sucesso!
                                        </Text>
                                    </Flex>
                                </>
                            )}
                            {section === "ANTIFRAUD_ANALYSIS" && (
                                <Flex
                                    w="100%"
                                    h="100%"
                                    align="center"
                                    justify="center"
                                    flexDir="column"
                                    gap={3}
                                    p={4}
                                >
                                    <Text
                                        fontSize="24px"
                                        fontWeight="bold"
                                        textAlign="center"
                                        mb={2}
                                    >
                                        Pagamento em análise
                                    </Text>
                                    <Text fontSize="15px" color="gray.700" maxW="340px" textAlign="center">
                                        Essa transação está passando por uma verificação adicional de segurança. O processamento pode levar um pouco mais de tempo.
                                    </Text>
                                    <Text fontSize="13px" color="gray.600" maxW="340px" textAlign="center">
                                        Transações acima de determinado valor passam por uma análise de segurança.
                                    </Text>
                                    <Text fontSize="13px" color="gray.600" maxW="340px" textAlign="center">
                                        Durante esse período, o pagamento fica em processamento e pode levar mais tempo para ser concluído.
                                    </Text>
                                    <Text fontSize="13px" color="gray.600" maxW="340px" textAlign="center">
                                        Para garantir a segurança da sua conta, algumas transações passam por uma verificação adicional. Isso pode aumentar o tempo de processamento, mas você será avisado assim que o pagamento for concluído.
                                    </Text>
                                </Flex>
                            )}
                        </>
                    </Flex>
        </Drawer>
    );
}